import React from "react";
import { FlexColumn, FlexColumnAlignJustifyCenter } from "@/components/Flex";
import LinearProgress, { LinearProgressBarProps } from "@/components/LinearProgress";
import { themeColors } from "@/utils/themeColors";
import { ExtraActionEnum, STREAK_ACTION_COLORS } from "@knowt/syncing/hooks/gamification/constants";
import { useUserXPStats, useXPStats } from "@knowt/syncing/hooks/gamification/utils";
import LevelsIcon from "@/components/icons/LevelsIcon";
import { iconSizes } from "@/utils/iconProps";

const XPAndLevelSummary = ({
    additionalPotentialXP,
    style,
    titleStyle,
    progressBarProps,
    xpCountStyle,
    levelSize = "small",
    titleProps,
}: {
    additionalPotentialXP?: number;
    style?: React.CSSProperties;
    titleStyle?: React.CSSProperties;
    xpCountStyle?: React.CSSProperties;
    progressBarProps?: Partial<LinearProgressBarProps>;
    titleProps?: React.HTMLAttributes<HTMLHeadingElement>;
    levelSize?: "small" | "large";
}) => {
    const { nextLevel, deltaXPForLevel, xpAccumulatedInThisLevel, progressToNextLevel, circleFilledPercentage } =
        useUserXPStats(additionalPotentialXP);

    const renderLevelProgressBar = () => {
        const potentialProgress = Math.min(
            100,
            ((xpAccumulatedInThisLevel + (additionalPotentialXP || 0)) / deltaXPForLevel) * 100
        );

        const levelProps =
            levelSize === "small"
                ? { radius: "5rem", size: iconSizes.MD_M }
                : levelSize === "large"
                  ? { radius: "7rem", size: iconSizes.LG + 4 }
                  : {};

        return (
            <div
                style={{ position: "relative", marginRight: "2.5rem" }}
                data-testid="auto-id-da407f29-4995-4d93-89b2-92e8a284ff87">
                <LinearProgress
                    variant={additionalPotentialXP ? "buffer" : "determinate"}
                    barColor={STREAK_ACTION_COLORS[ExtraActionEnum.LEVELS]}
                    bufferBarColor="#D9C8FF"
                    backgroundColor={themeColors.background}
                    borderRadius="3rem"
                    height="2rem"
                    width="100%"
                    value={circleFilledPercentage > 0 ? 100 : progressToNextLevel}
                    valueBuffer={potentialProgress}
                    {...progressBarProps}
                />
                <FlexColumnAlignJustifyCenter
                    style={{
                        width: levelProps.radius,
                        height: levelProps.radius,
                        borderRadius: "999px",
                        background: `linear-gradient(to right, ${
                            STREAK_ACTION_COLORS[ExtraActionEnum.LEVELS]
                        } ${circleFilledPercentage}%,${themeColors.background} ${circleFilledPercentage}%)`,
                        position: "absolute",
                        top: "50%",
                        right: 0,
                        transform: "translate(50%, -50%)",
                    }}>
                    <LevelsIcon level={nextLevel} size={levelProps.size} />
                </FlexColumnAlignJustifyCenter>
            </div>
        );
    };

    const renderXPUntilNextLevel = () => {
        return (
            <span
                className="secondaryText1"
                style={xpCountStyle}
                data-testid="auto-id-b2f04e29-cf9a-46d3-bc1f-671506949d0f">
                {xpAccumulatedInThisLevel}/{deltaXPForLevel}XP
                            </span>
        );
    };

    return (
        <FlexColumn style={{ rowGap: 6, ...style }}>
            <h5
                className="heading5"
                {...titleProps}
                style={{ ...titleStyle, ...titleProps?.style }}
                data-testid="auto-id-0a513bf7-ef49-41e3-a2c4-98508a672679">
                Level {nextLevel - 1}
            </h5>
            {renderLevelProgressBar()}
            {renderXPUntilNextLevel()}
        </FlexColumn>
    );
};

export const NonOwnerXPAndLevelSummary = ({
    xp,
    levelSize = "small",
    style,
    titleStyle,
    titleProps,
    progressBarProps,
    xpCountStyle,
}: {
    xp: number;
    style?: React.CSSProperties;
    titleStyle?: React.CSSProperties;
    xpCountStyle?: React.CSSProperties;
    progressBarProps?: Partial<LinearProgressBarProps>;
    titleProps?: React.HTMLAttributes<HTMLHeadingElement>;
    levelSize?: "small" | "large";
}) => {
    const { nextLevel, deltaXPForLevel, xpAccumulatedInThisLevel, progressToNextLevel, circleFilledPercentage } =
        useXPStats(xp);

    const renderLevelProgressBar = () => {
        const potentialProgress = Math.min(100, (xpAccumulatedInThisLevel / deltaXPForLevel) * 100);

        const levelProps =
            levelSize === "small"
                ? { radius: "5rem", size: iconSizes.MD_M }
                : levelSize === "large"
                  ? { radius: "7rem", size: iconSizes.LG + 4 }
                  : {};

        return (
            <div
                style={{ position: "relative", marginRight: "2.5rem" }}
                data-testid="auto-id-d6118237-7fce-4961-aa83-44cedb3451ff">
                <LinearProgress
                    variant={"determinate"}
                    barColor={STREAK_ACTION_COLORS[ExtraActionEnum.LEVELS]}
                    bufferBarColor="#D9C8FF"
                    backgroundColor={themeColors.background}
                    borderRadius="3rem"
                    height="2rem"
                    width="100%"
                    value={circleFilledPercentage > 0 ? 100 : progressToNextLevel}
                    valueBuffer={potentialProgress}
                    {...progressBarProps}
                />
                <FlexColumnAlignJustifyCenter
                    style={{
                        width: levelProps.radius,
                        height: levelProps.radius,
                        borderRadius: "999px",
                        background: `linear-gradient(to right, ${
                            STREAK_ACTION_COLORS[ExtraActionEnum.LEVELS]
                        } ${circleFilledPercentage}%,${themeColors.background} ${circleFilledPercentage}%)`,
                        position: "absolute",
                        top: "50%",
                        right: 0,
                        transform: "translate(50%, -50%)",
                    }}>
                    <LevelsIcon level={nextLevel} size={levelProps.size} />
                </FlexColumnAlignJustifyCenter>
            </div>
        );
    };

    const renderXPUntilNextLevel = () => {
        return (
            <span
                className="secondaryText1"
                style={xpCountStyle}
                data-testid="auto-id-bed5b1d4-42fb-4d9a-8dd1-c8864f022457">
                {xpAccumulatedInThisLevel}/{deltaXPForLevel}XP
                            </span>
        );
    };

    return (
        <FlexColumn style={{ rowGap: 6, ...style }}>
            <h5
                className="heading5"
                {...titleProps}
                style={{ ...titleStyle, ...titleProps?.style }}
                data-testid="auto-id-16663bbd-a803-454a-b2db-c761e7dd9445">
                Level {nextLevel - 1}
            </h5>
            {renderLevelProgressBar()}
            {renderXPUntilNextLevel()}
        </FlexColumn>
    );
};

export default XPAndLevelSummary;
